import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { courseApi } from '../services/api';
import { Course } from '../types';
import { ClockIcon, AcademicCapIcon, BookOpenIcon, CheckCircleIcon, ChipIcon, ChevronRightIcon, PlayIcon, StarIcon, UsersIcon } from '../components/icons';
import { useTheme } from '../contexts/ThemeContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface CourseModule {
  title: string;
  description: string;
  duration: number;
  topics: string[];
  learningObjectives: string[];
  assignments?: string[];
  resources?: string[];
}

interface CourseDetail {
  courseId: string;
  overview: string;
  prerequisites: string[];
  learningOutcomes: string[];
  modules: CourseModule[];
  totalDuration?: number;
  skillLevel: 'Beginner' | 'Intermediate' | 'Advanced';
  technologies: string[];
  certification: boolean;
}

const CourseDetailPage: React.FC = () => {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const { colors } = useTheme();
  const [course, setCourse] = useState<Course | null>(null);
  const [courseDetail, setCourseDetail] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModule, setActiveModule] = useState<number>(0);
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set([0]));

  // Temporarily disable animations to fix visibility issue
  const heroAnimation = { ref: null, className: 'animate-fadeIn' };
  const contentAnimation = { ref: null, className: 'animate-fadeInUp' };

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setError('Course ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [courseResponse, detailResponse] = await Promise.all([
          courseApi.getCourseById(courseId),
          courseApi.getCourseDetail(courseId)
        ]);
        
        setCourse(courseResponse.data.data.course);
        
        // Calculate total duration from modules
        const detail = detailResponse.data.data.courseDetail;
        const totalDuration = detail.modules.reduce((sum: number, module: CourseModule) => sum + module.duration, 0);
        setCourseDetail({ ...detail, totalDuration });
        
        setError(null);
      } catch (err) {
        setError('Failed to load course details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId]);

  const toggleModuleExpansion = (index: number) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: colors.background.default }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
      </div>
    );
  }

  if (error || !course || !courseDetail) {
    return (
      <div className="container mx-auto px-4 py-8 text-center" style={{ backgroundColor: colors.background.default }}>
        <p className="text-lg mb-4" style={{ color: colors.error.main || '#DC2626' }}>{error || 'Course not found'}</p>
        <Link
          to="/courses"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
          style={{
            backgroundColor: `${colors.accent.mint}20`,
            color: colors.accent.mint
          }}
        >
          <ChevronRightIcon className="w-5 h-5 rotate-180" />
          Back to Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" style={{ backgroundColor: colors.background.default }}>
      {/* Hero Section */}
      <section 
        className="relative overflow-hidden py-20"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primary.main}20 0%, ${colors.accent.mint}20 100%)`,
          backgroundColor: colors.background.default 
        }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.accent.mint}10 0%, transparent 70%)`,
              animationDelay: '0s'
            }}
          />
          <div 
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full animate-float"
            style={{ 
              background: `radial-gradient(circle, ${colors.primary.main}10 0%, transparent 70%)`,
              animationDelay: '2s'
            }}
          />
        </div>

        <div className={`container mx-auto px-4 relative z-10 ${heroAnimation.className}`}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Course Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4 text-sm">
                <span 
                  className="px-4 py-1 rounded-full font-medium"
                  style={{
                    backgroundColor: `${colors.accent.mint}20`,
                    color: colors.accent.mint
                  }}
                >
                  {course.category}
                </span>
                {courseDetail.certification && (
                  <span 
                    className="px-4 py-1 rounded-full font-medium"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                      color: colors.primary.contrastText
                    }}
                  >
                    Certificate Included
                  </span>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl font-bold" style={{ color: colors.text.primary }}>
                {course.title}
              </h1>

              <p className="text-lg leading-relaxed" style={{ color: colors.text.secondary }}>
                {courseDetail.overview}
              </p>

              {/* Instructor Info */}
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.primary.main}20` }}
                >
                  <UsersIcon className="w-6 h-6" style={{ color: colors.primary.main }} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: colors.text.secondary }}>Instructor</p>
                  <p className="font-semibold" style={{ color: colors.text.primary }}>{course.instructor.name}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                    color: colors.primary.contrastText
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
                  }}
                >
                  Enroll Now
                  <ChevronRightIcon className="w-5 h-5" />
                </Link>
                <button
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border-2 transition-all duration-300 hover:scale-105"
                  style={{
                    borderColor: colors.accent.mint,
                    color: colors.accent.mint,
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <PlayIcon className="w-5 h-5" />
                  Preview Course
                </button>
              </div>
            </div>

            {/* Course Image/Stats */}
            <div className="relative">
              <div 
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  boxShadow: `0 20px 40px -10px ${colors.accent.mint}30`
                }}
              >
                <img
                  src={course.thumbnail && course.thumbnail !== 'default-course.jpg' 
                    ? `http://localhost:5050/uploads/${course.thumbnail}` 
                    : `https://picsum.photos/seed/${course._id}/600/400`}
                  alt={course.title}
                  className="w-full h-[400px] object-cover"
                  onError={(e) => {
                    e.currentTarget.src = `https://picsum.photos/seed/${course._id}/600/400`;
                  }}
                />
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(to top, ${colors.background.paper}dd 0%, transparent 50%)`
                  }}
                />
                
                {/* Course Stats Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { icon: ClockIcon, label: 'Duration', value: `${courseDetail.totalDuration || 0}h` },
                      { icon: BookOpenIcon, label: 'Modules', value: courseDetail.modules.length },
                      { icon: AcademicCapIcon, label: 'Level', value: courseDetail.skillLevel },
                      { icon: StarIcon, label: 'Rating', value: '4.8' }
                    ].map((stat, index) => (
                      <div 
                        key={index}
                        className="text-center p-3 rounded-lg backdrop-blur-sm"
                        style={{ backgroundColor: `${colors.background.paper}90` }}
                      >
                        <stat.icon className="w-5 h-5 mx-auto mb-1" style={{ color: colors.accent.mint }} />
                        <p className="text-xs" style={{ color: colors.text.secondary }}>{stat.label}</p>
                        <p className="font-semibold text-sm" style={{ color: colors.text.primary }}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Price Tag */}
              <div 
                className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full flex items-center justify-center shadow-xl animate-pulse"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                }}
              >
                <div className="text-center" style={{ color: colors.primary.contrastText }}>
                  <p className="text-3xl font-bold">${course.price}</p>
                  <p className="text-xs">One-time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-20">
        <div className={`container mx-auto px-4 ${contentAnimation.className}`}>
          {/* Key Features Bar */}
          <div 
            className="rounded-2xl p-8 mb-12 shadow-xl"
            style={{
              backgroundColor: colors.background.paper,
              boxShadow: `0 10px 30px -10px ${colors.accent.mint}20`
            }}
          >
            <div className="grid md:grid-cols-3 gap-8">
              {/* Prerequisites */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text.primary }}>
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary.main}20` }}
                  >
                    <CheckCircleIcon className="w-4 h-4" style={{ color: colors.primary.main }} />
                  </div>
                  Prerequisites
                </h3>
                <ul className="space-y-2">
                  {courseDetail.prerequisites.map((prereq, index) => (
                    <li key={index} className="text-sm flex items-start gap-2" style={{ color: colors.text.secondary }}>
                      <span style={{ color: colors.accent.mint }}>•</span>
                      {prereq}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technologies */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text.primary }}>
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.accent.mint}20` }}
                  >
                    <ChipIcon className="w-4 h-4" style={{ color: colors.accent.mint }} />
                  </div>
                  Technologies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {courseDetail.technologies.map((tech, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${colors.accent.mint}15`,
                        color: colors.accent.mint
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Learning Outcomes Preview */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text.primary }}>
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary.main}20` }}
                  >
                    <AcademicCapIcon className="w-4 h-4" style={{ color: colors.primary.main }} />
                  </div>
                  What You'll Learn
                </h3>
                <ul className="space-y-2">
                  {courseDetail.learningOutcomes.slice(0, 3).map((outcome, index) => (
                    <li key={index} className="text-sm flex items-start gap-2" style={{ color: colors.text.secondary }}>
                      <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.accent.mint }} />
                      {outcome}
                    </li>
                  ))}
                  {courseDetail.learningOutcomes.length > 3 && (
                    <li className="text-sm font-medium" style={{ color: colors.accent.mint }}>
                      +{courseDetail.learningOutcomes.length - 3} more...
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Course Curriculum */}
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-center mb-8" style={{ color: colors.text.primary }}>
              Course Curriculum
            </h2>

            {courseDetail.modules.map((module, index) => (
              <div
                key={index}
                className="rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl"
                style={{
                  backgroundColor: colors.background.paper,
                  boxShadow: expandedModules.has(index) ? `0 10px 30px -10px ${colors.accent.mint}30` : `0 2px 10px -5px ${colors.border}40`
                }}
              >
                {/* Module Header */}
                <button
                  onClick={() => toggleModuleExpansion(index)}
                  className="w-full p-6 flex items-center justify-between transition-all duration-300"
                  style={{
                    backgroundColor: expandedModules.has(index) ? `${colors.primary.main}10` : 'transparent'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center font-semibold"
                      style={{
                        background: expandedModules.has(index) 
                          ? `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`
                          : `${colors.border}40`,
                        color: expandedModules.has(index) ? colors.primary.contrastText : colors.text.secondary
                      }}
                    >
                      {index + 1}
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
                        {module.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm" style={{ color: colors.text.secondary }}>
                        <span className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {module.duration} hours
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpenIcon className="w-4 h-4" />
                          {module.topics.length} topics
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon 
                    className={`w-6 h-6 transition-transform ${expandedModules.has(index) ? 'rotate-90' : ''}`}
                    style={{ color: colors.accent.mint }}
                  />
                </button>

                {/* Module Content */}
                {expandedModules.has(index) && (
                  <div className="p-6 pt-0 space-y-6 animate-fadeIn">
                    <p style={{ color: colors.text.secondary }}>{module.description}</p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Topics */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text.primary }}>
                          <BookOpenIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                          Topics Covered
                        </h4>
                        <ul className="space-y-2">
                          {module.topics.map((topic, topicIndex) => (
                            <li key={topicIndex} className="text-sm flex items-start gap-2" style={{ color: colors.text.secondary }}>
                              <span style={{ color: colors.accent.mint }}>▸</span>
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Learning Objectives */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text.primary }}>
                          <CheckCircleIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                          Learning Objectives
                        </h4>
                        <ul className="space-y-2">
                          {module.learningObjectives.map((objective, objIndex) => (
                            <li key={objIndex} className="text-sm flex items-start gap-2" style={{ color: colors.text.secondary }}>
                              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.accent.mint }} />
                              {objective}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Assignments & Resources */}
                    {(module.assignments || module.resources) && (
                      <div className="grid md:grid-cols-2 gap-6 pt-4 border-t" style={{ borderColor: `${colors.border}40` }}>
                        {module.assignments && (
                          <div>
                            <h4 className="font-semibold mb-2 text-sm" style={{ color: colors.text.primary }}>
                              📝 Assignments
                            </h4>
                            <ul className="space-y-1">
                              {module.assignments.map((assignment, aIndex) => (
                                <li key={aIndex} className="text-sm" style={{ color: colors.text.secondary }}>
                                  • {assignment}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {module.resources && (
                          <div>
                            <h4 className="font-semibold mb-2 text-sm" style={{ color: colors.text.primary }}>
                              📚 Resources
                            </h4>
                            <ul className="space-y-1">
                              {module.resources.map((resource, rIndex) => (
                                <li key={rIndex} className="text-sm" style={{ color: colors.text.secondary }}>
                                  • {resource}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div 
            className="mt-16 text-center p-8 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main}10 0%, ${colors.accent.mint}10 100%)`
            }}
          >
            <h3 className="text-2xl font-semibold mb-4" style={{ color: colors.text.primary }}>
              Ready to Start Your Learning Journey?
            </h3>
            <p className="mb-6" style={{ color: colors.text.secondary }}>
              Join hundreds of students who have transformed their careers with this course.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                color: colors.primary.contrastText
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
            >
              Enroll Now - ${course.price}
              <ChevronRightIcon className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetailPage;